import React, { useEffect, useRef, useState } from "react";
import { 
  HeartIcon, 
  ShieldCheckIcon, 
  SparklesIcon, 
  GlobeAltIcon, 
  CurrencyDollarIcon,
  BanknotesIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  BuildingLibraryIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ClockIcon,
  ChartBarIcon,
  BoltIcon,
  TrophyIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  HandRaisedIcon,
  UsersIcon,
  StarIcon
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";

const AboutPage = () => {
  const [activeReason, setActiveReason] = useState(null);
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  const reasons = [
    {
      id: 1,
      icon: "/assets/whychooseuse1.jpg",
      title: "We Love Freedom",
      description: "Even if we do not agree with you, we will defend your right to fundraise for the causes and people you love.",
      gradient: "from-blue-600 to-indigo-600",
      lightGradient: "from-blue-50 to-indigo-50",
      pattern: "bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/60 via-transparent to-transparent",
      accent: "",
      stat: "100%",
      statLabel: "free speech"
    },
    {
      id: 2,
      icon: "/assets/whychooseuse2.jpg",
      title: "We Love To Give Back",
      description: "Each month, we designate some of our operational expenses to 'Give Back' to active campaigns on our site.",
      gradient: "from-amber-600 to-orange-600",
      lightGradient: "from-amber-50 to-orange-50",
      pattern: "bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-100/60 via-transparent to-transparent",
      accent: "",
      stat: "₹2.5Cr+",
      statLabel: "given back"
    },
    {
      id: 3,
      icon: "/assets/whychooseuse3.jpg",
      title: "We Love Transparency",
      description: "We believe trust matters. That’s why we encourage clear updates, honest fundraising goals, and accountability for every campaign.",
      gradient: "from-rose-600 to-red-600",
      lightGradient: "from-rose-50 to-red-50",
      pattern: "bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-rose-100/60 via-transparent to-transparent",
      accent: "",
      stat: "100%",
      statLabel: "trusted giving"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-reason-reveal');
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -50px 0px' }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="w-full font-sans antialiased bg-white">
      
      {/* ===== CLEAN STYLES WITH COLOR ACCENTS ===== */}
      <style>
        {`
        @keyframes gentleFade {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes softPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
        
        .animate-gentle {
          animation: gentleFade 0.6s ease forwards;
        }
        
        .animate-soft-pulse {
          animation: softPulse 3s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }
        .delay-4 { animation-delay: 0.4s; }
        .delay-5 { animation-delay: 0.5s; }
        .opacity-0 { opacity: 0; }

        @keyframes reasonReveal {
          0% {
            opacity: 0;
            transform: translateY(40px) scale(0.96);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-reason-reveal {
          animation: reasonReveal 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        
        @keyframes floatOrb {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
            opacity: 0.3;
          }
          33% { 
            transform: translate(30px, -20px) scale(1.1); 
            opacity: 0.4;
          }
          66% { 
            transform: translate(-20px, 15px) scale(0.95); 
            opacity: 0.35;
          }
        }
        
        .animate-float-orb {
          animation: floatOrb 22s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: floatOrb 28s ease-in-out infinite reverse;
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        `}
      </style>

      {/* ===== HERO WITH DONATEPE BRANDING ===== */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-40 animate-float"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-emerald-50 rounded-full blur-3xl opacity-40 animate-float" style={{ animationDelay: '1s' }}></div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Brand badge */}
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-full border border-blue-100 mb-8 opacity-0 animate-gentle shadow-sm">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-soft-pulse"></span>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-soft-pulse" style={{ animationDelay: '0.5s' }}></span>
            </div>
            <span className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
              <SparklesIcon className="w-3.5 h-3.5 inline mr-1" />
              WELCOME TO DONATEPE
            </span>
          </div>
          
          {/* Main headline with brand */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-gray-900 tracking-tight mb-6 opacity-0 animate-gentle">
            <span className="inline-block">India's most trusted</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600 block mt-2 text-5xl md:text-6xl lg:text-7xl">
              fundraising platform
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed opacity-0 animate-gentle delay-1">
            At <span className="font-semibold text-blue-600">DonatePe</span>, we believe that every rupee should go to the cause. 
            That's why we're the only platform in India with <span className="font-semibold text-emerald-600">zero platform fees — forever.</span>
          </p>
          
          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 opacity-0 animate-gentle delay-2">
            <div className="flex items-center gap-2">
              <CheckBadgeIcon className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-gray-600">10,000+ campaigns</span>
            </div>
            <div className="flex items-center gap-2">
              <BanknotesIcon className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-gray-600">₹120Cr+ raised</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-gray-600">Zero platform fees</span>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <a
              href="/start-fundraiser"
              className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white rounded-xl text-base font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
            >
              <span className="flex items-center gap-2">
                Start your fundraiser on DonatePe
                <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ===== STATS WITH DONATEPE IMPACT ===== */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">DonatePe in numbers</span>
            <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mt-2">The impact you've made possible</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { number: '₹120Cr+', label: 'Total funds raised', sub: 'across all campaigns', color: 'blue', icon: BanknotesIcon },
              { number: '2M+', label: 'Unique donors', sub: 'and growing daily', color: 'emerald', icon: HeartIcon },
              { number: '10k+', label: 'Campaigns launched', sub: 'from 1,200+ cities', color: 'blue', icon: RocketLaunchIcon },
              { number: '₹0', label: 'Platform fees', sub: 'always 100% free', color: 'emerald', icon: ShieldCheckIcon }
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group">
                  <Icon className={`w-8 h-8 mb-2 text-${stat.color}-600`} />
                  <div className={`text-2xl md:text-3xl font-bold text-${stat.color}-600`}>{stat.number}</div>
                  <div className="font-medium text-gray-900 text-sm mt-1">{stat.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{stat.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== DONATEPE STORY ===== */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            
            {/* Left - Story with brand */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <SparklesIcon className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">The DonatePe story</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-medium text-gray-900">
                We started DonatePe because 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600 block mt-2">
                  platform fees didn't feel right.
                </span>
              </h2>
              
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  In 2022, our friend needed ₹5 lakhs for a medical emergency. He raised the money, 
                  but the platform took ₹25,000 as fees. That's when we realized — fundraising platforms 
                  were profiting from people in need.
                </p>
                <p>
                  So we built <span className="font-semibold text-blue-600">DonatePe</span>. A platform where 100% of what you raise goes to your cause. 
                  No platform fees. No hidden charges. Just a direct connection between those who need help 
                  and those who want to give.
                </p>
                <p className="bg-gradient-to-r from-blue-50 to-emerald-50 p-5 rounded-xl border-l-4 border-emerald-500">
                  <span className="font-medium text-gray-900">Today, DonatePe helps thousands of families</span> raise crores for medical treatments, 
                  education, and community causes. And we're just getting started.
                </p>
              </div>
              
              {/* Founder quote */}
              <div className="flex items-start gap-3 pt-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-full flex items-center justify-center text-blue-600 font-bold">SK</div>
                <div>
                  <p className="text-sm text-gray-500 italic">"We built DonatePe for our friend. But we stayed for every family that needs help."</p>
                  <p className="text-xs text-gray-400 mt-1">— Sai Krishna, CEO</p>
                </div>
              </div>
            </div>
            
            {/* Right - Visual timeline with brand */}
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
                  DonatePe milestones
                </h3>
                <div className="space-y-4">
                  {[
                    { year: '2022', event: 'DonatePe founded in Hyderabad', icon: RocketLaunchIcon },
                    { year: '2024', event: 'First 1000 campaigns completed', icon: TrophyIcon },
                    { year: '2025', event: 'Crossed ₹120Cr in total funds raised', icon: BanknotesIcon },
                    { year: '2026', event: 'Launched 24/7 fund access', icon: ClockIcon },
                    { year: '2026', event: 'Now serving 50,000+ campaigners', icon: HeartIcon }
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-gray-50 rounded-full flex items-center justify-center">
                          <Icon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-blue-600">{item.year}</span>
                          </div>
                          <p className="text-sm text-gray-600">{item.event}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Featured in */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">As featured in</p>
                <div className="grid grid-cols-3 gap-4">
                  {['YourStory', 'Economic Times', 'India Today'].map((pub, i) => (
                    <div key={i} className="text-xs font-medium text-gray-600 bg-white px-3 py-2 rounded-full text-center border border-gray-100">
                      {pub}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US - DIRECT CODE (NOT IMPORTED) ===== */}
      <section ref={sectionRef} className="relative bg-white py-2 md:py-2 lg:py-2 overflow-hidden">
        
        {/* Premium animated background - warm, inviting */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 via-white to-gray-50/30"></div>
          
          {/* Floating orbs - one for each reason */}
          <div className="absolute top-20 -left-20 w-[500px] h-[500px] bg-gradient-to-r from-blue-100/40 to-indigo-100/40 rounded-full blur-3xl animate-float-orb"></div>
          <div className="absolute bottom-40 -right-20 w-[500px] h-[500px] bg-gradient-to-r from-amber-100/40 to-orange-100/40 rounded-full blur-3xl animate-float-orb"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-rose-100/30 to-red-100/30 rounded-full blur-3xl animate-pulse-slow"></div>
          
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #0d9488 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header - Emotional, human */}
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/70 backdrop-blur-xl rounded-full border border-white/30 shadow-lg shadow-gray-200/20 mb-6">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
              </span>
              <span className="text-sm font-semibold bg-gradient-to-r from-gray-700 to-gray-600 bg-clip-text text-transparent">
                More than a platform
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
              Built on{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-amber-600">
                  belief & compassion
                </span>
                <span className="absolute -bottom-3 left-0 w-full h-3 bg-gradient-to-r from-rose-200/30 to-amber-200/30 blur-md"></span>
                <span className="absolute -bottom-3 left-0 w-full h-1 bg-gradient-to-r from-rose-500 to-amber-500 rounded-full"></span>
              </span>
            </h2>
            
            <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
              We're not just a fundraising site. We're a community that stands with you — in every cause, every prayer, every victory.
            </p>
          </div>

          {/* Reasons - Premium, emotional design */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 max-w-6xl mx-auto">
            {reasons.map((reason, index) => (
              <div
                key={reason.id}
                ref={(el) => (cardRefs.current[index] = el)}
                className="group relative opacity-0 translate-y-10 transition-all duration-1000 cursor-pointer"
                style={{ transitionDelay: `${index * 150}ms` }}
                onMouseEnter={() => setActiveReason(reason.id)}
                onMouseLeave={() => setActiveReason(null)}
              >
                
                {/* Container with organic shape */}
                <div className="relative">
                  
                  {/* Animated glow ring - reason specific */}
                  <div className={`absolute -inset-3 bg-gradient-to-r ${reason.gradient} rounded-[40px] blur-2xl opacity-0 group-hover:opacity-25 group-hover:scale-105 transition-all duration-700`}></div>
                  
                  {/* Main content - No card, just flowing design */}
                  <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-9 flex flex-col items-center text-center border border-white/40 shadow-2xl shadow-gray-200/40 transition-all duration-500 group-hover:shadow-3xl group-hover:-translate-y-2 group-hover:bg-white">
                    
                    {/* Animated pattern overlay */}
                    <div className={`absolute inset-0 ${reason.pattern} opacity-0 group-hover:opacity-40 rounded-3xl transition-opacity duration-700`}></div>
                    
                    {/* Decorative top accent - hand-drawn feel */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-white/80 to-white/20 group-hover:w-16 group-hover:from-rose-400/80 group-hover:to-amber-400/80 transition-all duration-500 rounded-full"></div>
                    
                    {/* Icon container - artistic, sculptural */}
                    <div className="relative mb-7">
                      
                      {/* Multi-layered background glow */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${reason.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-30 transition-all duration-700 scale-90 group-hover:scale-110`}></div>
                      <div className={`absolute inset-0 ${reason.lightGradient} rounded-3xl opacity-0 group-hover:opacity-60 transition-all duration-700 scale-95 group-hover:scale-105`}></div>
                      
                      {/* Icon frame - organic shape */}
                      <div className="relative w-28 h-28 lg:w-32 lg:h-32 flex items-center justify-center">
                        <div className="absolute inset-0 bg-white rounded-3xl rotate-6 group-hover:rotate-12 transition-all duration-500 shadow-lg opacity-0 group-hover:opacity-100"></div>
                        <div className="absolute inset-0 bg-white rounded-3xl -rotate-3 group-hover:-rotate-6 transition-all duration-500 shadow-lg opacity-0 group-hover:opacity-100"></div>
                        <div className="relative z-10 w-24 h-24 lg:w-28 lg:h-28 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-xl flex items-center justify-center group-hover:scale-110 transition-all duration-500">
                          <img
                            src={reason.icon}
                            alt={reason.title}
                            className="w-14 h-14 lg:w-16 lg:h-16 object-contain drop-shadow-lg transition-all duration-500 group-hover:drop-shadow-2xl group-hover:scale-110"
                          />
                        </div>
                      </div>
                      
                      {/* Floating accent dot */}
                      <div className={`absolute -bottom-2 -right-2 w-6 h-6 bg-gradient-to-r ${reason.gradient} rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-500 scale-0 group-hover:scale-100`}>
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>

                    {/* Title with elegant underline */}
                    <div className="relative mb-4">
                      <h3 className={`text-2xl lg:text-3xl font-bold mb-2 transition-all duration-300 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r ${reason.gradient}`}>
                        {reason.title}
                      </h3>
                      
                      {/* Hand-drawn underline effect */}
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-gray-200 group-hover:w-20 group-hover:bg-gradient-to-r group-hover:from-rose-400 group-hover:to-amber-400 transition-all duration-500"></div>
                    </div>
                    
                    {/* Description - warm, human */}
                    <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-6 group-hover:text-gray-700 transition-colors duration-300">
                      {reason.description}
                    </p>
                    
                    {/* Stats & accent - minimal, meaningful */}
                    <div className="flex items-center justify-between w-full pt-4 border-t border-gray-100/80 group-hover:border-gray-200 transition-colors duration-300">
                      
                      {/* Stat badge */}
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${reason.gradient} animate-pulse`}></div>
                        <div className="text-left">
                          <span className={`text-sm font-bold bg-gradient-to-r ${reason.gradient} bg-clip-text text-transparent`}>
                            {reason.stat}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">
                            {reason.statLabel}
                          </span>
                        </div>
                      </div>
                      
                      {/* Accent tag */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-gray-400 group-hover:text-gray-600 transition-colors">
                          {reason.accent}
                        </span>
                        <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-rose-500 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Active indicator */}
                    {activeReason === reason.id && (
                      <div className="absolute -top-2 -right-2">
                        <span className="relative flex h-4 w-4">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-60"></span>
                          <span className="relative inline-flex rounded-full h-4 w-4 bg-rose-500"></span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Banner - Community message */}
          <div className="mt-20 lg:mt-24 text-center">
            <div className="relative inline-flex flex-col items-center gap-6 max-w-3xl mx-auto">
              
              {/* Decorative elements */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent"></div>
                <span className="text-rose-500 text-sm font-medium uppercase tracking-[0.3em]">Together we stand</span>
                <div className="w-12 h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
              </div>
              
              {/* Heartfelt message */}
              <p className="text-2xl md:text-3xl font-light text-gray-700 italic leading-relaxed">
                "We believe that every cause deserves a voice, 
                <br />every fundraiser deserves support, 
                <br />and every person deserves hope."
              </p>
              
              {/* Signature */}
              <div className="flex items-center gap-3 mt-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm text-gray-500 font-medium">— The DonatePe family</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SUCCESS STORIES ===== */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">DonatePe stories</span>
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mt-2 mb-3">
              Real people, real impact
            </h2>
            <p className="text-gray-500">
              Thousands of families have raised what they needed on DonatePe.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Priya M.',
                location: 'Bengaluru',
                story: 'Raised ₹8 lakhs for mother\'s cancer treatment in 45 days.',
                amount: '₹8L',
                icon: HeartSolidIcon
              },
              {
                name: 'Arun K.',
                location: 'Chennai',
                story: 'Funded his startup with ₹15 lakhs from 200+ donors.',
                amount: '₹15L',
                icon: RocketLaunchIcon
              },
              {
                name: 'Fatima S.',
                location: 'Mumbai',
                story: 'Built a community library with ₹3.5 lakhs raised locally.',
                amount: '₹3.5L',
                icon: BookOpenIcon
              }
            ].map((story, i) => {
              const Icon = story.icon;
              return (
                <div key={i} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100 hover:border-blue-100 transition-all duration-300">
                  <Icon className="w-8 h-8 mb-3 text-blue-600" />
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-gray-900">{story.name}</span>
                    <span className="text-xs text-gray-400">• {story.location}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{story.story}</p>
                  <div className="inline-block px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full">
                    Raised {story.amount}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== TEAM WITH DONATEPE ===== */}
      <section className="py-16 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    
    <div className="text-center max-w-2xl mx-auto mb-12">
      <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">The team</span>
      <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mt-2 mb-3">
        Meet the people behind DonatePe
      </h2>
      <p className="text-gray-500">
        We're engineers, designers, and problem-solvers who care about impact.
      </p>
    </div>
    
    <div className="flex justify-center">
      <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-xs">
        {[
          { name: 'Sai krishna ', role: 'Founder & CEO', color: 'blue', initial: 'SK', bio: '', icon: BriefcaseIcon },
        ].map((person, index) => {
          const Icon = person.icon;
          return (
            <div key={index} className="text-center group">
              <div className={`w-24 h-24 mx-auto mb-3 bg-gradient-to-br from-${person.color}-100 to-${person.color}-200 rounded-2xl flex items-center justify-center text-${person.color}-600 text-2xl font-medium group-hover:scale-105 group-hover:rotate-3 transition-all duration-300 shadow-sm`}>
                <Icon className="w-8 h-8" />
              </div>
              <h3 className="font-medium text-gray-900">{person.name}</h3>
              <p className="text-xs text-blue-600 mt-0.5">{person.role}</p>
              <p className="text-xs text-gray-400 mt-1">{person.bio}</p>
            </div>
          );
        })}
      </div>
    </div>
    
    <div className="text-center mt-8">
      <p className="text-sm text-gray-500">
        And a team of 25+ amazing people working to make DonatePe better every day.
      </p>
    </div>
  </div>
</section>

      {/* Add grid pattern style */}
      <style>{`
        .bg-grid-pattern {
          background-image: linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 40px 40px;
        }
      `}</style>
      
    </div>
  );
};

export default AboutPage;